import { QRCodeSVG } from "qrcode.react";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Linkedin, Twitter, Facebook, MessageCircle, Copy, Code, Download } from "lucide-react";
import { SHARE } from "@/constants/testIds";
import { downloadCertificatePDF } from "@/lib/certificatePdf";

/**
 * Always use the canonical LaneX Academy domain for cert verification links + QR codes
 * — so a certificate scanned from any environment (preview, staging, prod) points to a
 * LaneX-branded verification URL, never to the underlying host (e.g. emergentagent.com).
 */
function certOrigin() {
  const env = process.env.REACT_APP_CERTIFICATE_URL;
  if (env) return env.replace(/\/$/, "");
  const host = typeof window !== "undefined" ? window.location.origin : "";
  // Never let an Emergent preview host leak into a public cert URL.
  if (/emergentagent\.com$/i.test(host) || /emergent\.sh$/i.test(host)) {
    return "https://lanexacademy.com";
  }
  return host || "https://lanexacademy.com";
}

/**
 * Share panel used on Certificates and public Verify page.
 * Provides LinkedIn "Add to LinkedIn" flow, X, Facebook, WhatsApp, copy, and embed code.
 */
export function CertificateShare({ cert, size = "md" }) {
  const url = `${certOrigin()}/verify/${cert.cert_id}`;
  const shareText = `I just earned my ${cert.path_title} certificate from LaneX Academy! Verify: ${url}`;

  const linkedinAddUrl = () => {
    // LinkedIn Add to Profile deep-link (works even without organizationId — falls back to CE form)
    const params = new URLSearchParams({
      startTask: "CERTIFICATION_NAME",
      name: `${cert.path_title} — LaneX Academy`,
      organizationName: "LaneX Academy",
      issueYear: String(new Date(cert.issued_at).getFullYear()),
      issueMonth: String(new Date(cert.issued_at).getMonth() + 1),
      certUrl: url,
      certId: cert.cert_id,
    });
    return `https://www.linkedin.com/profile/add?${params.toString()}`;
  };

  const openIn = (href) => {
    window.open(href, "_blank", "noopener,noreferrer,width=720,height=680");
  };

  const copyLink = () => {
    navigator.clipboard.writeText(url);
    toast.success("Public verification link copied");
  };

  const copyEmbed = () => {
    const code = `<iframe src="${url}?embed=1" width="360" height="240" style="border:0;border-radius:12px;overflow:hidden" title="LaneX Academy verified certificate"></iframe>`;
    navigator.clipboard.writeText(code);
    toast.success("Embed code copied");
  };

  const iconSize = size === "sm" ? "h-3.5 w-3.5" : "h-4 w-4";
  const btn = "inline-flex items-center gap-1.5 rounded-full border border-border bg-background px-3 py-1.5 font-mono text-xs uppercase tracking-widest hover:border-primary transition";

  return (
    <div className="flex flex-wrap gap-2">
      <Button
        data-testid={SHARE.linkedin(cert.cert_id)}
        onClick={() => openIn(linkedinAddUrl())}
        className="rounded-full btn-neon font-mono text-xs uppercase tracking-widest"
        size="sm"
      >
        <Linkedin className={`mr-1.5 ${iconSize}`} /> Add to LinkedIn
      </Button>
      <button
        data-testid={SHARE.twitter(cert.cert_id)}
        className={btn}
        onClick={() => openIn(`https://twitter.com/intent/tweet?text=${encodeURIComponent(shareText)}`)}
      >
        <Twitter className={iconSize} /> X
      </button>
      <button
        data-testid={SHARE.facebook(cert.cert_id)}
        className={btn}
        onClick={() => openIn(`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}`)}
      >
        <Facebook className={iconSize} /> Facebook
      </button>
      <button
        data-testid={SHARE.whatsapp(cert.cert_id)}
        className={btn}
        onClick={() => openIn(`https://api.whatsapp.com/send?text=${encodeURIComponent(shareText)}`)}
      >
        <MessageCircle className={iconSize} /> WhatsApp
      </button>
      <button data-testid={SHARE.copy(cert.cert_id)} className={btn} onClick={copyLink}>
        <Copy className={iconSize} /> Copy link
      </button>
      <button data-testid={SHARE.embed(cert.cert_id)} className={btn} onClick={copyEmbed}>
        <Code className={iconSize} /> Embed
      </button>
      <button
        data-testid={`cert-pdf-${cert.cert_id}`}
        className={btn + " border-primary/50 text-primary"}
        onClick={async () => {
          try {
            await downloadCertificatePDF(cert);
            toast.success("Certificate PDF downloaded");
          } catch { toast.error("Could not generate PDF"); }
        }}
      >
        <Download className={iconSize} /> Download PDF
      </button>
    </div>
  );
}

export function CertificateQR({ cert, size = 96 }) {
  const url = `${certOrigin()}/verify/${cert.cert_id}`;
  return (
    <div className="rounded-lg border border-border bg-white p-2">
      <QRCodeSVG value={url} size={size} level="M" includeMargin={false} />
    </div>
  );
}

export function VerifiedBadge({ className = "" }) {
  return (
    <div className={`inline-flex items-center gap-1.5 rounded-full border border-accent/40 bg-accent/10 px-3 py-1 ${className}`}>
      <span className="grid h-4 w-4 place-items-center rounded-full bg-accent text-accent-foreground font-bold text-[10px]">✓</span>
      <span className="font-mono text-[0.65rem] uppercase tracking-widest text-accent">Verified by LaneX Academy</span>
    </div>
  );
}
